import type { AgentTool } from './tools';
import { truncateOutput } from './tools';
import { redactSecrets } from './redact';
import { forComponent } from './logging';
import { sandboxArgv, findSandboxWrapper, type OsSandboxOptions } from './shell.sandbox';
import { JobRegistry, type JobHandle } from './tools.jobs';
import { DETACH_OPTIONS, IS_WIN, defaultShell, killTree, shellFor, winTreeCpuSeconds, type ShellSpec } from './platform';

/**
 * Real shell tool — node-only, OPT-IN, and deliberately NOT edge-portable.
 *
 *   ⚠️ Unlike the default VFS `bash` (a sandboxed JS interpreter over the virtual filesystem),
 *   this spawns a REAL `/bin/sh` process. It can run `bun`, `git`, `ssh`, scripts, deploys —
 *   and, by the same token, it is NOT sandboxed: only cwd-binding constrains it. It is a
 *   deliberate host escalation, kept out of `defaultTools()`/`toolRegistry()` and out of the
 *   edge-safe `src/index.ts` (same policy as `mcp.client.ts`). A host opts in explicitly:
 *
 *     tools: [...defaultTools(), makeRealShellTool({ cwd: nodeDiskRoot })]
 *
 *   Mirrors `tools.web.ts`: a factory with an injectable `spawn` (tests + edge never import
 *   node:child_process), an options bag, abort + timeout honored, output capped. Safety beyond
 *   cwd-binding is the host's to add (e.g. a PermissionPolicy `decision:'ask'` per command, or
 *   an OS sandbox wrapper) — see mind/03-roadmap.md "OS-level access — capability tiers".
 */

const log = forComponent('shell');

/** Normalize shell output for return: trim trailing newlines, mask secret values, then size-truncate.
 *  Redaction runs BEFORE truncation so a masked tail can't smuggle a secret past the line cap. */
const clean = (s: string): string => truncateOutput(redactSecrets(s.replace(/\n+$/, '')));

/** The slice of node's `child_process.spawn` we depend on — injectable so tests supply a fake. */
export type SpawnFn = (
  command: string,
  args: string[],
  options: {
    cwd?: string;
    env?: Record<string, string | undefined>;
    signal?: AbortSignal;
    /** stdio layout. We force stdin to /dev/null so a child can't block on (or steal) the REPL's input. */
    stdio?: ['ignore', 'pipe', 'pipe'];
    /** Run in a new session/process group (setsid) — detaches from the controlling terminal. See DETACHED. */
    detached?: boolean;
    /** Windows only: no console window for the child (src/platform.ts DETACH_OPTIONS). */
    windowsHide?: boolean;
    /** Windows only: pass the argv verbatim (cmd.exe's own quoting — src/platform.ts ShellSpec). */
    windowsVerbatimArguments?: boolean;
  },
) => SpawnedProcess;

/** Minimal `ChildProcess` surface this tool uses. */
export interface SpawnedProcess {
  stdout?: { on(ev: 'data', cb: (chunk: any) => void): void } | null;
  stderr?: { on(ev: 'data', cb: (chunk: any) => void): void } | null;
  on(ev: 'close', cb: (code: number | null) => void): void;
  on(ev: 'error', cb: (err: Error) => void): void;
  kill(signal?: string): void;
  /** Child PID — present on the real node child; used to signal the whole process group on abort. */
  pid?: number;
}

/**
 * Detach every spawned child from the REPL's controlling terminal.
 *
 *   `stdio: ['ignore', 'pipe', 'pipe']` — stdin is /dev/null (clean EOF; nothing to block on).
 *   `detached: true` — setsid() puts the child in its OWN session with NO controlling tty.
 *
 * Without this a child inherits the agent's tty: an interactive prompt (`sudo`, `ssh`, a git
 * credential helper) opens `/dev/tty` directly and then RACES the REPL's raw-mode input reader for
 * the user's keystrokes — a deadlock that also captures whatever the user types (e.g. a password)
 * into the agent instead of the program. Detached, those programs find no tty and fail FAST with a
 * legible error ("sudo: a terminal is required …") which the model can act on (tell the user to run
 * it via `!`), instead of hanging until the 120s timeout. Side benefit: the child is a process-group
 * leader, so abort/timeout can reap the whole subtree, not just /bin/sh.
 * Windows has no setsid/process groups: there the child is hidden instead of detached (`detached`
 * would open a console window) and its tree is reaped with `taskkill /T` — see src/platform.ts. */
const DETACHED = { stdio: ['ignore', 'pipe', 'pipe'] as ['ignore', 'pipe', 'pipe'], ...DETACH_OPTIONS };

/** Signal a child's WHOLE process group (`-pid`). Children are group leaders (DETACHED), so `proc.kill`
 *  hits only /bin/sh and orphans descendants; this reaps the subtree. Best-effort — the group may
 *  already be gone, and the fake spawn in tests has no real pid. Returns false when nothing was signaled.
 *  (Windows: the tree by parent pid — `killTree` in src/platform.ts.) */
function killGroup(proc: SpawnedProcess | undefined, signal: 'SIGTERM' | 'SIGKILL'): boolean {
  return killTree(proc?.pid, signal);
}

export interface RealShellOptions {
  /** Working directory the shell is bound to (typically a NodeDiskFilesystem `baseDir`). Required. */
  cwd: string;
  /** Shell binary for `-c` (e.g. the user's `$SHELL`). Default `/bin/sh`. Only honored when NOT
   *  OS-sandboxed (the sandbox wrapper pins its own shell). */
  shell?: string;
  /** Override the spawner (tests inject a fake; default lazily imports node:child_process). */
  spawn?: SpawnFn;
  /** Per-command wall-clock cap (kill on overrun). Default 120s. */
  timeoutMs?: number;
  /** Ceiling for the model-supplied `timeoutMs` argument (a long build can ask for more than the
   *  default, but not for an unbounded turn). Default 10min. */
  maxTimeoutMs?: number;
  /** Extra env merged over the (optionally scrubbed) base env for the child. */
  env?: Record<string, string>;
  /** Strip likely-secret vars (API keys, tokens, cloud creds) from the child's env. Default ON.
   *  The FS jail does NOT contain a real process, so this is the seam that keeps `echo $ANTHROPIC_API_KEY`
   *  from leaking the host's secrets to a spawned command. `false` passes `process.env` through verbatim. */
  redactEnv?: boolean;
  /** Job registry enabling `Shell({background:true})` (long-running processes). Pair with `makeShellJobTools`. */
  registry?: ShellJobRegistry;
  /** Tier-1 OS sandbox: wrap /bin/sh in sandbox-exec (macOS) / bwrap (Linux) — writes confined to
   *  cwd+tmp, network blocked unless granted. `true` = defaults; commands FAIL (don't silently run
   *  unsandboxed) if no wrapper exists on this platform. See src/shell.sandbox.ts. */
  osSandbox?: boolean | Partial<OsSandboxOptions>;
  /** Does a background job outlive the current run? False on one-shot hosts (the process exits and
   *  `killOnExit` reaps the job), which the Shell tool's description must say rather than promise
   *  cross-turn survival it won't get. Default true. */
  jobsSurviveRun?: boolean;
}

/** What to spawn for one command: binary, argv and any shell-specific spawn options (Windows cmd.exe). */
interface ShellArgv { bin: string; args: string[]; spawnOptions?: ShellSpec['spawnOptions'] }
const shellArgv = (sh: ShellSpec, command: string): ShellArgv => ({ bin: sh.bin, args: sh.args(command), ...(sh.spawnOptions ? { spawnOptions: sh.spawnOptions } : {}) });

/** Resolve the (bin,args) to spawn for `command`, honoring the optional OS sandbox.
 *  Throws when sandboxing was requested but this platform has no wrapper — fail closed. */
async function spawnArgvFor(command: string, cwd: string, osSandbox?: boolean | Partial<OsSandboxOptions>): Promise<ShellArgv> {
  if (!osSandbox) return shellArgv(defaultShell(), command);
  const opts = osSandbox === true ? {} : osSandbox;
  const wrapper = await findSandboxWrapper();
  const wrapped = wrapper ? sandboxArgv(command, cwd, opts, process.platform, process.env.TMPDIR) : null;
  if (!wrapped) throw new Error(`OS sandbox requested but no wrapper available on ${process.platform} (need sandbox-exec or bwrap)`);
  return wrapped;
}

/** Env var names that look like secrets and are dropped before spawning (unless redactEnv:false). */
const SECRET_ENV_RE = /(_API_KEY|_TOKEN|_SECRET|_PASSWORD|_PRIVATE_KEY|^AWS_|^GITHUB_TOKEN$|^OPENAI_|^ANTHROPIC_|^GOOGLE_|^GEMINI_|^GROQ_|^NPM_TOKEN$)/i;

/** Build the child's env: `process.env` minus likely-secrets (when redacting), plus explicit `env`. */
function childEnv(opts: { env?: Record<string, string>; redactEnv?: boolean }): Record<string, string | undefined> {
  const base: Record<string, string | undefined> = {};
  const redact = opts.redactEnv !== false; // default ON
  for (const [k, v] of Object.entries(process.env)) if (!(redact && SECRET_ENV_RE.test(k))) base[k] = v;
  return { ...base, ...opts.env };
}

/** Lazily resolve node's spawn (kept out of any eager edge import path). */
let _spawn: SpawnFn | undefined;
async function nodeSpawn(): Promise<SpawnFn> {
  if (!_spawn) _spawn = (await import('node:child_process')).spawn as unknown as SpawnFn;
  return _spawn;
}

// ---------------------------------------------------------------------------
// Background jobs — long-running processes the agent starts, polls, and kills.
// ---------------------------------------------------------------------------
export type JobStatus = 'running' | 'exited' | 'killed' | 'error';

export interface ShellJobConfig {
  cwd: string;
  spawn?: SpawnFn;
  env?: Record<string, string>;
  redactEnv?: boolean;
  /** Tail buffer cap per job (bytes); older output is dropped. Default 256 KB. Ignored when `jobs` is shared. */
  maxBuffer?: number;
  /** Kill all jobs on process exit (the CLI sets this; tests leave it off to avoid global handlers). */
  killOnExit?: boolean;
  /** Tier-1 OS sandbox for background jobs too (same semantics as RealShellOptions.osSandbox). */
  osSandbox?: boolean | Partial<OsSandboxOptions>;
  /**
   * Called once when a background job reaches a terminal state. Claude-Code parity: a background task
   * that finishes has to reach the MODEL, or `background:true` is a write-only hole — the model starts
   * a job, forgets it, and never collects the result. The host wires this to `agent.inject(...)`, which
   * folds the notice in at the next step boundary (and re-opens the turn if the model was about to stop).
   * A job REAPED as stuck is reported too (status `killed` + `reason`).
   */
  onExit?: JobExitHandler;
  /** The generic job registry these jobs live in. Share the Agent's (`AgentOptions.jobs`) so one
   *  JobStatus/JobOutput surface and one liveness reaper cover shell, MCP and delegated tools alike. */
  jobs?: JobRegistry;
  /** Cumulative CPU seconds of a process group — the "is a silent job still working?" probe used before an
   *  ADOPTED job is reaped as idle. Default: `ps` (psGroupCpuSeconds); return undefined when unknown. */
  cpuTime?: (pgid: number) => Promise<number | undefined>;
}

export interface JobExitNotice { id: string; command: string; status: JobStatus; exitCode?: number; tail: string; reason?: string }
export type JobExitHandler = (n: JobExitNotice) => void;

/** The exact text a completion notice becomes. Shared so every host (CLI, presets, embedders) pushes the
 *  same thing back to the model instead of each inventing its own wording. */
export function formatJobExit(n: JobExitNotice): string {
  return `[background job ${n.id} ${n.status}${n.exitCode != null ? ` exit ${n.exitCode}` : ''}${n.reason ? `: ${n.reason}` : ''}] \`${n.command}\`\n`
    + (n.tail ? `${n.tail}\n` : '(no output)\n')
    + `Read the full output with ShellOutput({id:"${n.id}"}).`;
}

/** Parse `ps` TIME (`[[dd-]hh:]mm:ss[.cc]`) to seconds. */
export function parsePsTime(t: string): number | undefined {
  const m = t.trim().match(/^(?:(\d+)-)?(?:(\d+):)?(\d+):(\d+(?:\.\d+)?)$/);
  if (!m) return undefined;
  return Number(m[1] ?? 0) * 86400 + Number(m[2] ?? 0) * 3600 + Number(m[3]) * 60 + Number(m[4]);
}

/** Summed CPU seconds of the job's live processes: its process group (`pgid` = the detached leader's pid) PLUS every
 *  descendant of that pid — a child that moved to its own group (`timeout`, `setsid`, some build tools) still works for
 *  the job and must count. One `ps` call, C locale (stable number format); macOS + Linux. Undefined when nothing is
 *  left or ps is unavailable — the reaper then treats CPU as unknown and does NOT kill on it. */
export async function psGroupCpuSeconds(pgid: number): Promise<number | undefined> {
  if (IS_WIN) { // no ps / process groups: the tree under the job's root pid (src/platform.ts)
    try { return await winTreeCpuSeconds(pgid); } catch (e) { log.debug(`cpu probe failed for ${pgid}: ${e}`); return undefined; }
  }
  try {
    const { execFile } = await import('node:child_process');
    const out = await new Promise<string>((res, rej) => execFile('ps', ['-A', '-o', 'pid=,ppid=,pgid=,time='], { timeout: 5_000, env: { ...process.env, LC_ALL: 'C' } }, (e, so) => (e ? rej(e) : res(String(so)))));
    return sumTreeCpu(out, pgid);
  } catch (e) { log.debug(`ps cpu probe failed for group ${pgid}: ${e}`); return undefined; }
}

/** Pure part of `psGroupCpuSeconds`: parse `pid ppid pgid time` rows, sum the group ∪ descendants of `root`. */
export function sumTreeCpu(psOut: string, root: number): number | undefined {
  const rows = psOut.split('\n').map((l) => l.trim().split(/\s+/)).filter((r) => r.length >= 4)
    .map(([pid, ppid, pgid, time]) => ({ pid: Number(pid), ppid: Number(ppid), pgid: Number(pgid), sec: parsePsTime(time) }));
  const inJob = new Set(rows.filter((r) => r.pgid === root || r.pid === root).map((r) => r.pid));
  for (let grew = true; grew;) { grew = false; for (const r of rows) if (!inJob.has(r.pid) && inJob.has(r.ppid)) { inJob.add(r.pid); grew = true; } }
  let total: number | undefined;
  for (const r of rows) if (inJob.has(r.pid) && r.sec != null) total = (total ?? 0) + r.sec;
  return total;
}

interface ShellMeta { command: string; exitCode?: number; proc?: SpawnedProcess }

/**
 * Per-session registry of background `/bin/sh` jobs. Backs `Shell({background:true})`, foreground-deadline
 * adoption, and the `ShellOutput`/`ShellStatus`/`ShellKill` tools. A thin OS layer over the generic
 * `JobRegistry` (src/tools.jobs.ts): output ring, liveness, notices and the stuck-job reaper live there.
 *
 * Liveness policy: an EXPLICIT `background:true` job is never idle-reaped (a quiet dev server is healthy).
 * An ADOPTED job (a foreground command that outran its timeout) is: after the `shell` idle window with no
 * output AND no CPU progress across its process group, it is SIGTERMed (SIGKILL after the grace period)
 * and reported as `killed: stuck: idle Ns`.
 */
export class ShellJobRegistry {
  readonly jobs: JobRegistry;
  private meta = new Map<string, ShellMeta>();
  constructor(private cfg: ShellJobConfig) {
    this.jobs = cfg.jobs ?? new JobRegistry(cfg.maxBuffer ? { maxBuffer: cfg.maxBuffer } : {});
    if (cfg.killOnExit && typeof process !== 'undefined') process.once('exit', () => this.killAll());
  }

  async start(command: string): Promise<string> {
    const h = this.track(command, { idleMs: 0 }); // explicit background: the model asked for a long-lived process
    try {
      const spawn = this.cfg.spawn ?? (await nodeSpawn());
      const argv = this.cfg.osSandbox ? await spawnArgvFor(command, this.cfg.cwd, this.cfg.osSandbox) : shellArgv(defaultShell(), command);
      this.attach(h, spawn(argv.bin, argv.args, { cwd: this.cfg.cwd, env: childEnv(this.cfg), ...DETACHED, ...argv.spawnOptions }));
    } catch (e: any) {
      h.chunk(`failed to spawn: ${e?.message ?? e}`);
      h.finish('error', undefined, e?.message ?? String(e));
    }
    return h.id;
  }

  private track(command: string, extra: { idleMs?: number; seed?: string; cpu?: boolean }): JobHandle {
    const meta: ShellMeta = { command };
    let id = '';
    const h = this.jobs.track({
      kind: 'shell', label: command, seed: extra.seed, idleMs: extra.idleMs,
      cpuTime: extra.cpu ? async () => (meta.proc?.pid ? (this.cfg.cpuTime ?? psGroupCpuSeconds)(meta.proc.pid) : undefined) : undefined,
      onKill: () => this.signal(meta),
      onExit: (n) => this.notifyExit(id, meta, n.status, n.reason),
      notify: true,
    });
    id = h.id;
    this.meta.set(id, meta);
    return h;
  }

  private attach(h: JobHandle, proc: SpawnedProcess): void {
    const meta = this.meta.get(h.id)!;
    meta.proc = proc;
    const append = (chunk: any) => h.chunk(typeof chunk === 'string' ? chunk : chunk?.toString?.('utf8') ?? '');
    proc.stdout?.on('data', append);
    proc.stderr?.on('data', append);
    proc.on('error', (err: any) => { if (h.status === 'running') { append(`\n[error] ${err?.message ?? err}`); h.finish('error', undefined, err?.message ?? String(err)); } });
    proc.on('close', (code: number | null) => { if (h.status === 'running') { meta.exitCode = code ?? undefined; h.finish('done'); } });
  }

  /** SIGTERM the whole group (bg children are detached group leaders — /bin/sh alone would orphan a forked
   *  server), then SIGKILL after the grace period if it is still there. Falls back to the pid for fakes. */
  private signal(meta: ShellMeta): void {
    const proc = meta.proc;
    if (!killGroup(proc, 'SIGTERM')) { try { proc?.kill('SIGTERM'); } catch { /* already gone */ } }
    if (!proc?.pid) return;
    let closed = false;
    proc.on('close', () => { closed = true; });
    const t = setTimeout(() => { if (!closed) killGroup(proc, 'SIGKILL'); }, this.jobs.options.killGraceMs);
    (t as any).unref?.();
  }

  /** Fire `onExit` at most once per job (the registry guarantees one terminal state), with the tail so the model can act without a second round-trip. */
  private notifyExit(id: string, meta: ShellMeta, status: string, reason?: string): void {
    // A kill the model/host asked for is not news to it; a REAPED job (reason `stuck: …`) is.
    if (!this.cfg.onExit || (status === 'killed' && !reason?.startsWith('stuck'))) return;
    try {
      this.cfg.onExit({ id, command: meta.command, status: shellStatus(status), exitCode: meta.exitCode, tail: clean(this.jobs.output(id) ?? '').slice(-4000), ...(reason && status !== 'done' ? { reason } : {}) });
    } catch { /* a host notification must never take down the job registry */ }
  }

  /**
   * Wire (or rewire) the completion callback AFTER construction. A host that only gets its agent handle
   * once the Agent is constructed (the library's own `fullAgentOptions` preset, any embedder) could not
   * pass `onExit` up front, so background completions were silently REPL-only. Set it here instead.
   */
  setOnExit(fn: JobExitHandler | undefined): void { this.cfg.onExit = fn; }

  /** Whether a finished job will actually be reported to the model. The Shell tool's description reads
   *  this so it can't promise a completion notice on a host that discards it. */
  get notifiesOnExit(): boolean { return !!this.cfg.onExit; }

  /** Current tail output for a job (null = no such job). */
  output(id: string): string | null { return this.meta.has(id) ? this.jobs.output(id) : null; } // null too once the shared registry pruned it

  status(id: string): { status: JobStatus; exitCode?: number; bytes: number; reason?: string } | null {
    const m = this.meta.get(id);
    const j = m && this.jobs.get(id);
    if (!m || !j) { this.meta.delete(id); return null; } // pruned from the shared registry (keepFinished): gone, not a crash
    return { status: shellStatus(j.status), exitCode: m.exitCode, bytes: j.bytes, ...(j.reason ? { reason: j.reason } : {}) };
  }

  list(): Array<{ id: string; command: string; status: JobStatus }> {
    return [...this.meta].filter(([id]) => this.jobs.get(id)).map(([id, m]) => ({ id, command: m.command, status: shellStatus(this.jobs.get(id)!.status) }));
  }

  /**
   * Take over an ALREADY-RUNNING child as a background job. A foreground command that outruns its
   * timeout is not necessarily a hung command — killing it throws away work that was nearly done and,
   * worse, a launcher that spawned its own detached worker leaves that worker running with nothing
   * tracking it. Adopting hands the model a handle instead: the command keeps going, its completion
   * is reported like any other job, and `seed` carries the output produced before the handoff.
   * Unlike an explicit background job it IS liveness-checked (idle window + CPU probe): nobody asked for
   * a long-lived process here, so one that goes silent and CPU-idle is stuck, not serving.
   */
  adopt(command: string, proc: SpawnedProcess, seed = ''): string {
    const h = this.track(command, { seed, cpu: true });
    this.attach(h, proc);
    return h.id;
  }

  kill(id: string, reason = 'killed by ShellKill'): boolean {
    return this.meta.has(id) && this.jobs.kill(id, reason);
  }

  killAll(): void { for (const id of this.meta.keys()) this.jobs.kill(id, 'agent exiting'); }
}

const shellStatus = (s: string): JobStatus => (s === 'done' ? 'exited' : s === 'running' ? 'running' : s === 'killed' ? 'killed' : 'error');

/** Build an opt-in real-shell tool bound to `options.cwd`. */
export function makeRealShellTool(options: RealShellOptions): AgentTool {
  const defaultTimeoutMs = options.timeoutMs ?? 120_000;
  const maxTimeoutMs = Math.max(options.maxTimeoutMs ?? 600_000, defaultTimeoutMs);
  // The background promise must match what THIS host actually does — a lie here is what makes
  // `background:true` a black hole the model trusts. Evaluated lazily (the registry's onExit may be wired
  // after the tool is built, via setOnExit).
  // NOTIFICATION and SURVIVAL are independent facts governed by different things — `registry.onExit`
  // (is a completion pushed to the model?) and `jobsSurviveRun` (does the host outlive this run?). Reading
  // both off `notifiesOnExit` made the unwired preset tell the model a job "does NOT survive the end of
  // this run" while an identically-configured wired registry said the opposite. Each clause states what
  // actually governs it.
  const backgroundDoc = () => {
    const base = 'Set `background:true` for long-running processes (servers, watchers) and for anything that already timed out in the foreground — '
      + 'returns a job id immediately; poll with ShellOutput/ShellStatus, stop with ShellKill. ';
    const notify = options.registry?.notifiesOnExit
      ? 'Its completion is reported back to you when it finishes.'
      : 'NOTE: nothing will tell you when it finishes — poll ShellOutput/ShellStatus yourself, or you will never see its result.';
    const survival = options.jobsSurviveRun === false
      ? 'The job does NOT survive the end of this run, so do not stop while you still need its result.'
      : 'It keeps running across turns.';
    return `${base}${notify} ${survival}`;
  };
  return {
    name: 'Shell',
    writes: true,
    // Rebind for an isolated child agent (git worktree): same policy/env/timeouts, new cwd. The
    // `registry` is deliberately dropped — it is bound to the PARENT's cwd (and, in hosts like
    // shraga-ee, to the parent's session), so a background job started from the child would run
    // outside the child's isolation and report into the parent. Background stays a parent capability
    // (`makeShellJobTools`' companions drop out of an isolated child entirely — see their `withCwd`).
    // Only `run` is rebuilt: everything else is carried over from THIS instance, because a host may
    // have mutated the tool AFTER construction (shraga-ee renames `Shell` -> `Bash` and its system
    // prompt says `Bash` everywhere). Rebuilding from `options` alone silently dropped that rename,
    // so a worktree child advertised `Shell` while being told to call `Bash` — an unknown-tool error
    // on its first call, and a hard failure for an agentType def whose allowlist names `Bash`.
    // `description` is the ONE thing taken from the rebound tool instead: it is derived from the
    // options and states whether `background:true` works — the rebind drops the registry, so carrying
    // the parent's text over would advertise a background capability the child does not have. (The
    // spread also collapses the getter below to a plain value, which is why this override is explicit.)
    withCwd(cwd: string): AgentTool {
      const rebound = makeRealShellTool({ ...options, cwd, registry: undefined });
      return { ...this, description: rebound.description, run: rebound.run, withCwd: rebound.withCwd };
    },
    get description() {
      return `Run a shell command via ${shellFor(options.shell).label} in the working directory. ` + windowsShellNote(shellFor(options.shell)) +
      'Executes any installed binary — ls, cat, grep, git, bun, node, curl, scripts, etc. ' +
      'Returns combined stdout+stderr; non-zero exits are prefixed `[exit N]`. ' +
      'Runs non-interactively with no terminal (stdin is /dev/null): commands that prompt for input ' +
      'fail fast rather than hang — for privileged actions use a non-interactive flag (e.g. `sudo -n`), ' +
      'or ask the user to run the command themselves. ' +
      (options.registry?.adopt
        ? `A command still running after ${defaultTimeoutMs}ms is NOT killed — it is handed to a background job and you get its id, so you can keep checking on it (\`[still running]\`). Pass \`timeoutMs\` (max ${maxTimeoutMs}) to wait longer in the foreground, and always bound network commands yourself (e.g. \`curl -m 10\`). `
        : `Each command is killed after ${defaultTimeoutMs}ms (result \`[exit 124]\` with whatever output it produced) — pass \`timeoutMs\` (max ${maxTimeoutMs}) for a legitimately slower command, and always bound network commands yourself (e.g. \`curl -m 10\`). `) +
      backgroundDoc();
    },
    // Declared so transports above the agent loop can size their own deadline with headroom (see AgentTool.maxDurationMs).
    maxDurationMs: maxTimeoutMs,
    parameters: {
      type: 'object',
      required: ['command'],
      properties: {
        command: { type: 'string', description: 'the shell command line to execute' },
        background: { type: 'boolean', description: 'run detached and return a job id immediately (for servers/watchers/long builds)' },
        timeoutMs: { type: 'number', description: 'wall-clock cap for THIS command, overriding the default (clamped to the tool max)' },
      },
    },
    async run({ command, background, timeoutMs: askedTimeoutMs, timeout: aliasTimeout }, ctx) {
      const cmd = String(command ?? '');
      // `timeout` is an UNADVERTISED alias for `timeoutMs`, same unit (ms). Models reach for the
      // shorter name from muscle memory (observed: composer-2.5 sending `{"timeout":180000}`), and
      // an unknown key is silently dropped — the command then dies at the 120s default while the
      // model believes it asked for 180s and reads an unexplained kill. Accepted, never documented,
      // so the schema still teaches exactly one name.
      const asked = Number.isFinite(askedTimeoutMs) ? askedTimeoutMs : aliasTimeout;
      const timeoutMs = Number.isFinite(asked) && Number(asked) > 0
        ? Math.min(Number(asked), maxTimeoutMs)
        : defaultTimeoutMs;
      if (!cmd.trim()) return '[exit 1] empty command';
      if (background) {
        if (!options.registry) return 'Error: background execution is not enabled in this host (no job registry).';
        const id = await options.registry.start(cmd);
        return `Started background job ${id}. Poll output with ShellOutput({id:"${id}"}), check ShellStatus({id:"${id}"}), stop with ShellKill({id:"${id}"}).`;
      }
      const spawn = options.spawn ?? (await nodeSpawn());
      // Sandbox-off keeps this path await-free (after spawn resolution) so an abort racing the call
      // start still lands before listener registration, exactly as pre-sandbox semantics.
      let argv: ShellArgv = shellArgv(shellFor(options.shell), cmd);
      if (options.osSandbox) {
        try {
          argv = await spawnArgvFor(cmd, options.cwd, options.osSandbox);
        } catch (e: any) {
          return `[exit 1] ${e?.message ?? e}`; // fail closed — never run unsandboxed when sandboxing was asked for
        }
      }
      // Compose abort: the run's signal (ctx.signal) OR our per-command timeout both kill the child.
      const ctl = new AbortController();
      const onAbort = () => ctl.abort();
      if (ctx.signal) { if (ctx.signal.aborted) ctl.abort(); else ctx.signal.addEventListener('abort', onAbort, { once: true }); }
      let timedOut = false;
      // Set by the promise body once the child exists, so the timer can hand it over instead of killing it.
      let onDeadline: () => void = () => { timedOut = true; ctl.abort(); };
      const timer = setTimeout(() => { onDeadline(); }, timeoutMs);
      // The child is its own process-group leader (DETACHED): node's `signal` kills only /bin/sh, so on
      // abort also SIGKILL the whole group to reap any descendants (see killGroup).
      // Incremental output → ctx.emit (when the host listens), coalesced to ≥250ms / ≥1KB batches
      // so a chatty child doesn't spam hooks. Redacted per batch (the final result is re-redacted
      // whole — a secret split across batch boundaries can slip the per-batch pass, hence the cap
      // on what consumers may do with chunks: display/digest, never persistence).
      let pend = '';
      let flushTimer: ReturnType<typeof setTimeout> | null = null;
      const flushEmit = (ctx: { emit?: (s: string) => void }) => {
        if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
        if (pend) { ctx.emit?.(redactSecrets(pend)); pend = ''; }
      };
      try {
        return await new Promise<string>((resolve) => {
          let out = '';
          let settled = false;
          const finish = (s: string) => { if (settled) return; settled = true; resolve(s); };
          let proc: SpawnedProcess;
          try {
            // Windows: reap the tree BEFORE node's own `signal` handler (registered inside spawn) terminates
            // the shell — `taskkill /T` finds the tree by parent pid, so it must run while the root is alive.
            if (IS_WIN) ctl.signal.addEventListener('abort', () => killGroup(proc, 'SIGKILL'), { once: true });
            proc = spawn(argv.bin, argv.args, { cwd: options.cwd, env: childEnv(options), signal: ctl.signal, ...DETACHED, ...argv.spawnOptions });
          } catch (e: any) {
            return finish(`[exit 1] failed to spawn shell: ${e?.message ?? e}`);
          }
          // Reap the detached group when the CANCEL fires (node's `signal` only hits /bin/sh).
          if (ctl.signal.aborted) killGroup(proc, 'SIGKILL');
          else if (!IS_WIN) ctl.signal.addEventListener('abort', () => killGroup(proc, 'SIGKILL'), { once: true });
          // Deadline reached: hand the still-running child to the job registry rather than killing it.
          // Only a host with a registry can do this — elsewhere the deadline stays a kill, because a
          // child nothing tracks is worse than a dead one.
          const registry = options.registry;
          if (registry?.adopt) {
            onDeadline = () => {
              timedOut = true;
              if (settled) return;
              flushEmit(ctx);
              // The registry attaches its own collectors from here on; ours would only grow a buffer
              // nobody reads. `off` is not part of the minimal stream shape, hence the guarded call.
              (proc.stdout as any)?.off?.('data', collect);
              (proc.stderr as any)?.off?.('data', collect);
              const id = registry.adopt!(cmd, proc, out);
              finish(handoffFor(timeoutMs, id, clean(out), !!registry.notifiesOnExit));
            };
          }
          const collect = (chunk: any) => {
            const s = typeof chunk === 'string' ? chunk : chunk?.toString?.('utf8') ?? '';
            out += s;
            if (ctx.emit && !settled) {
              pend += s;
              if (pend.length >= 1024) flushEmit(ctx);
              else flushTimer ??= setTimeout(() => flushEmit(ctx), 250);
            }
          };
          proc.stdout?.on('data', collect);
          proc.stderr?.on('data', collect);
          proc.on('error', (err: any) => {
            // AbortError fires here when ctl.abort() kills the child — report timeout vs cancel.
            if (err?.name === 'AbortError' || ctl.signal.aborted) return finish(reasonFor(timedOut, timeoutMs, clean(out)));
            log.debug('shell spawn error', err);
            finish(`[exit 1] ${err?.message ?? err}${out ? '\n' + clean(out) : ''}`);
          });
          proc.on('close', (code: number | null) => {
            flushEmit(ctx); // drain the coalesce buffer before settling (still pre-resolve, so ctx.emit is live)
            if (ctl.signal.aborted) return finish(reasonFor(timedOut, timeoutMs, clean(out)));
            const body = clean(out);
            if (code && code !== 0) return finish(`[exit ${code}]${body ? '\n' + body : ''}`);
            finish(body || '(command succeeded, no output)');
          });
        });
      } finally {
        clearTimeout(timer);
        if (flushTimer) clearTimeout(flushTimer); // no emits after the call settles
        ctx.signal?.removeEventListener('abort', onAbort);
      }
    },
  };
}

/** Windows only (empty elsewhere): which syntax the shell speaks and how paths look in it, so the model
 *  doesn't send bash to PowerShell or guess at drive letters. */
export function windowsShellNote(sh: ShellSpec): string {
  if (!IS_WIN) return '';
  if (sh.kind === 'sh') return 'This is Windows: the shell is bash (POSIX syntax); drive paths are written /c/Users/… or C:/Users/…, and Windows programs (.exe/.cmd) run directly. ';
  if (sh.kind === 'powershell') return 'This is Windows and the shell is PowerShell — use PowerShell syntax (`;` not `&&`, `$env:VAR`, Get-ChildItem/Select-String), not bash. ';
  return 'This is Windows and the shell is cmd.exe — use cmd syntax (`&&`, `%VAR%`, dir/type/findstr), not bash. ';
}

/** Deadline message when the child was ADOPTED rather than killed: the work continues, so say how to follow it. */
function handoffFor(timeoutMs: number, id: string, body: string, notifies: boolean): string {
  const head = `[still running] exceeded ${timeoutMs}ms, so it was handed to background job ${id} — NOT killed, it is still going `
    + `(it is only stopped if it goes silent with no CPU activity for a long time, and then you are told why). `
    + `Check on it with ShellOutput({id:"${id}"}) / ShellStatus({id:"${id}"}), stop it with ShellKill({id:"${id}"}). `
    + (notifies
      ? 'Its completion will be reported to you, so you may continue with other work meanwhile.'
      : 'Nothing will tell you when it finishes — poll it yourself before you rely on its result.');
  return body ? `${head}\nOutput so far:\n${body}` : head;
}

/** Abort message: timeout vs external cancel, preserving any partial output. */
function reasonFor(timedOut: boolean, timeoutMs: number, body: string): string {
  // A foreground timeout is a DEAD END unless the result says how to get out of it: re-issuing the same
  // command is the one thing that cannot work, and it is exactly what a model does when the message is
  // just a code. Name the two recoveries (background, or narrow the command).
  const head = timedOut
    ? `[exit 124] timed out after ${timeoutMs}ms (killed). Re-running this command unchanged will time out again. `
      + `Either re-run it with background:true (returns a job id immediately; poll with ShellOutput/ShellStatus, `
      + `stop with ShellKill) or narrow it so it can finish — bound it, scope it, or ask for less.`
    : '[exit 130] cancelled (killed)';
  return body ? `${head}\nPartial output before the kill:\n${body}` : head;
}

const NO_JOB = (id: string) => `Error: no background job '${id}'. Use ShellStatus with no id to list jobs, or start one with Shell({background:true}).`;

/** Build the background-job companion tools (ShellOutput / ShellStatus / ShellKill) over a registry.
 *  Each closes over THIS registry, i.e. this agent's jobs in this agent's cwd, so none of them can be
 *  rebound to an isolated child's root: `withCwd` returns `undefined` and they are dropped from the
 *  child's toolbelt. That keeps the capability coherent — the child's `Shell` cannot START a background
 *  job (its registry is dropped), so it must not be able to inspect or SIGTERM the parent's either. */
export function makeShellJobTools(registry: ShellJobRegistry): AgentTool[] {
  const idParam = { type: 'object', properties: { id: { type: 'string', description: 'the job id from Shell({background:true})' } } };
  const dropOnRebind = { withCwd: () => undefined };
  return [
    {
      ...dropOnRebind,
      name: 'ShellOutput',
      description: 'Read the accumulated output (tail) of a background Shell job by id.',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        const out = registry.output(String(id));
        if (out == null) return NO_JOB(String(id));
        const st = registry.status(String(id))!;
        return `[${st.status}${st.exitCode != null ? ` exit ${st.exitCode}` : ''}${st.reason ? `: ${st.reason}` : ''}]\n${clean(out) || '(no output yet)'}`;
      },
    },
    {
      ...dropOnRebind,
      name: 'ShellStatus',
      description: 'Status of a background Shell job (running/exited/killed + exit code). Omit `id` to list all jobs.',
      parameters: idParam,
      async run({ id }) {
        if (!id) {
          const jobs = registry.list();
          return jobs.length ? jobs.map((j) => `${j.id}  ${j.status}  ${j.command}`).join('\n') : '(no background jobs)';
        }
        const st = registry.status(String(id));
        return st ? `${st.status}${st.exitCode != null ? ` (exit ${st.exitCode})` : ''}${st.reason ? ` — ${st.reason}` : ''} · ${st.bytes} byte(s) buffered` : NO_JOB(String(id));
      },
    },
    {
      ...dropOnRebind,
      name: 'ShellKill',
      description: 'Stop a running background Shell job by id (SIGTERM to its process group, SIGKILL if it ignores that).',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        return registry.kill(String(id)) ? `Killed job ${id}.` : NO_JOB(String(id));
      },
    },
  ];
}
